import React from 'react';
import { Card } from '@dopomogai/ui';
import { TierBadge } from './TierBadge';
import { AvatarTierBackground } from './AvatarTierBackground';

export type ShowcaseTier = 'trainee' | 'junior' | 'middle' | 'senior';

type Props = {
  avatars: {
    trainee?: string;
    junior?: string;
    middle?: string;
    senior?: string;
    poster?: string;
  };
  currentTier: ShowcaseTier;
  ariaLabel?: string;
  className?: string;

  // Control visual footprint
  size?: 'sm' | 'md';

  // Render without the outer Card (so the parent can group into a single Card)
  variant?: 'card' | 'flat';
};

const looksLikeVideo = (url?: string) => {
  if (!url) return false;
  const u = url.toLowerCase();
  return u.endsWith('.webm') || u.endsWith('.mp4') || u.includes('video');
};

export function AvatarStageLite({
  avatars,
  currentTier,
  ariaLabel = 'Agent avatar',
  className,
  size = 'md',
  variant = 'card',
}: Props) {
  const src =
    currentTier === 'senior'
      ? avatars.senior
      : currentTier === 'middle'
      ? avatars.middle
      : currentTier === 'junior'
      ? avatars.junior
      : avatars.trainee;

  const poster = avatars.poster;

  const mediaSizeClasses =
    size === 'sm'
      ? 'w-full h-[240px] md:h-[260px] object-contain p-2 bg-white'
      : 'w-full aspect-square object-contain p-2 bg-white';

  const Root: React.ElementType = variant === 'card' ? Card : 'div';
  const rootProps =
    variant === 'card'
      ? {
          noPadding: true,
          className: [
            'relative overflow-hidden rounded-xl',
            'bg-white',
            'border-0',
            className || '',
          ].join(' '),
        }
      : {
          className: [
            'relative overflow-hidden rounded-xl',
            'bg-white',
            className || '',
          ].join(' '),
        };

  return (
    <Root {...(rootProps as any)}>
      {/* Tier-specific background */}
      <AvatarTierBackground tier={currentTier} intensity={currentTier === 'senior' ? 'bold' : 'subtle'} />

      {/* Tier badge (with logo) */}
      <div className="absolute z-10 m-2">
        <TierBadge tier={currentTier} size="sm" />
      </div>

      {looksLikeVideo(src) ? (
        <video
          key={`vid-${currentTier}`}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          className={mediaSizeClasses}
          aria-label={ariaLabel}
        >
          {src?.toLowerCase().endsWith('.webm') && <source src={src} type="video/webm" />}
          {src?.toLowerCase().endsWith('.mp4') && <source src={src} type="video/mp4" />}
        </video>
      ) : (
        <img
          key={`img-${currentTier}`}
          src={src || poster}
          alt={ariaLabel}
          className={mediaSizeClasses}
          loading="lazy"
          decoding="async"
        />
      )}
    </Root>
  );
}

export default AvatarStageLite;
